// Reads an uploaded customer or cheque list: CSV (any delimiter Excel writes: , or ;) or Excel .xlsx.
// The first row holds the column names (spec 03 R-23).
export type Sheet = { head: string[]; rows: string[][] };
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;

/** CSV with quoted fields ("a, b", doubled "" inside quotes), CRLF or LF, and a UTF-8 BOM. */
export function parseCSV(txt: string): Sheet {
  const text = txt.replace(/^﻿/, '');
  const first = text.split(/\r?\n/, 1)[0] || '';
  const sep = (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  const clean = rows.filter(r => r.some(c => c.trim() !== ''));
  return { head: (clean[0] || []).map(h => h.trim()), rows: clean.slice(1) };
}

const two = (n: number) => String(n).padStart(2, '0');
const cellText = (v: unknown): string =>
  v == null ? '' : v instanceof Date ? `${two(v.getDate())}/${two(v.getMonth() + 1)}/${v.getFullYear()}` : typeof v === 'number' ? String(v) : String(v);

export class FileError extends Error {
  constructor(public code: 'type' | 'size' | 'empty' | 'unreadable') { super(code); }
}

export async function readTableFile(file: File): Promise<Sheet> {
  if (file.size > MAX_IMPORT_BYTES) throw new FileError('size');
  const csv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
  const xlsx = /\.xlsx$/i.test(file.name);
  if (!csv && !xlsx) throw new FileError('type');
  let sheet: Sheet;
  try {
    if (csv) sheet = parseCSV(await file.text());
    else {
      const { readSheet } = await import('read-excel-file/browser');
      const rows = (await readSheet(file)) as unknown[][];
      const all = rows.map(r => r.map(cellText)).filter(r => r.some(c => c.trim() !== ''));
      sheet = { head: (all[0] || []).map(h => h.trim()), rows: all.slice(1) };
    }
  } catch { throw new FileError('unreadable'); }
  if (!sheet.head.length || !sheet.rows.length) throw new FileError('empty');
  return sheet;
}
